<script setup lang="ts">
import type { CountryCode } from 'libphonenumber-js';

import { useVuelidate } from '@vuelidate/core';
import { ref, computed, watch, nextTick, onMounted, useId } from 'vue';

import { formatIncompletePhoneNumber } from '@/shared/lib/formatters';
import { parsePhoneNumber, parseIncompletePhoneNumber, phoneRules, requiredRule } from '@/shared/lib/validators';

interface Props {
  label?: string;
  defaultCountry?: CountryCode;
  normalization?: 'national' | 'international';
  modelValue?: string | number | null;
  placeholder?: string;
  disabled?: boolean;
  variantIsError?: boolean;
  required?: boolean;
  autofocus?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  defaultCountry: 'RU',
  normalization: 'national',
  modelValue: '',
  placeholder: '+7 ___ ___-__-__',
  disabled: false,
  variantIsError: false,
  required: false,
  autofocus: false,
});

interface Emits {
  (e: 'update:modelValue', value: string): void;
  (e: 'validity-change', isValid: boolean): void;
  (e: 'focus', event: FocusEvent): void;
  (e: 'blur', event: FocusEvent): void;
}

const emit = defineEmits<Emits>();

const inputRef = ref<HTMLInputElement | null>(null);
const inputId = useId();
const isFocused = ref(false);

/**
 * Нормализует ввод:
 * - Очищает от спецсимволов через библиотеку (включая экзотические цифры)
 * - +89... → +79... (российский мобильный номер с ошибочным «+» перед 8)
 * - 8... → +7... (российский междугородный формат)
 * - 9... → +7... (мобильные без кода)
 * - 7... → +7... (код страны без +)
 * - 00... → +... (международный префикс)
 */
function normalizeInput(value: string): string {
  // Сначала очищаем от лишних символов через библиотеку
  // Это надёжнее обрабатывает экзотические цифры и спецсимволы
  let cleaned = parseIncompletePhoneNumber(value);

  if (props.normalization === 'international') {
    if (cleaned.startsWith('00')) return '+' + cleaned.substring(2);
    return cleaned && !cleaned.startsWith('+') ? '+' + cleaned : cleaned;
  }
  if (props.defaultCountry !== 'RU') return cleaned;

  // Затем делаем локализованные преобразования для России/СНГ
  if (cleaned.startsWith('+89')) {
    if (import.meta.env.DEV) {
      console.warn('[FIX:admin-phone-plus-eight]', {
        event: 'normalized-russian-mobile-prefix',
        inputLength: cleaned.length,
      });
    }

    cleaned = '+7' + cleaned.substring(2);
  } else if (cleaned.startsWith('8')) {
    cleaned = '+7' + cleaned.substring(1);
  } else if (cleaned.startsWith('9')) {
    cleaned = '+7' + cleaned;
  } else if (cleaned.startsWith('7') && !cleaned.startsWith('+')) {
    cleaned = '+' + cleaned;
  } else if (cleaned.startsWith('00')) {
    cleaned = '+' + cleaned.substring(2);
  }

  return cleaned;
}

/**
 * Форматирует номер для отображения
 */
function formatForDisplay(value: string): string {
  if (!value) {
    return '';
  }

  const normalized = normalizeInput(value);

  // Пробуем распарсить и отформатировать полный валидный номер
  const phoneNumber = parsePhoneNumber(normalized, props.defaultCountry);

  if (phoneNumber && phoneNumber.isValid()) {
    return phoneNumber.formatInternational();
  }

  // Если номер неполный - используем formatIncompletePhoneNumber
  // Библиотека сама определит страну и применит нужный формат
  return formatIncompletePhoneNumber(normalized, props.defaultCountry);
}

/**
 * Внутреннее значение для валидации (чистый номер)
 */
const internalValue = computed({
  get() {
    return normalizeInput(String(props.modelValue ?? ''));
  },
  set(newValue: string) {
    const normalized = normalizeInput(newValue);
    emit('update:modelValue', normalized);
  },
});

/**
 * Отображаемое значение (форматированный номер)
 */
const displayValue = ref('');

// Синхронизация displayValue с modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    if (!isFocused.value) {
      displayValue.value = formatForDisplay(String(newValue ?? ''));
    }
  },
  { immediate: true },
);

/**
 * Правила валидации (как в AppInput)
 */
const validationRules = computed(() => {
  const rules: Record<string, unknown> = {};

  if (props.required) {
    rules.required = requiredRule.required;
  }

  // Кастомный валидатор для международных номеров
  rules.validPhone = phoneRules.validPhone;

  return { internalValue: rules };
});

const vuelidate = useVuelidate(validationRules, { internalValue });

/**
 * Показывать ошибку
 */
const showErrorBorder = computed(() => props.variantIsError || vuelidate.value.$error);

/**
 * Обработка ввода
 */
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const cursorPosition = target.selectionStart || 0;
  const oldValue = displayValue.value;
  const newValue = target.value;

  // Сбрасываем ошибки валидации при начале редактирования
  vuelidate.value.$reset();

  // Нормализуем и обновляем значение
  const normalized = normalizeInput(newValue);
  internalValue.value = normalized;

  // Форматируем для отображения
  displayValue.value = formatForDisplay(normalized);

  // Восстанавливаем позицию курсора после форматирования
  void nextTick(() => {
    if (inputRef.value) {
      const diff = displayValue.value.length - oldValue.length;
      const newPosition = cursorPosition + diff;
      inputRef.value.setSelectionRange(newPosition, newPosition);
    }
  });
};

/**
 * Обработка фокуса
 */
const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;

  // Сбрасываем ошибки валидации при получении фокуса
  // Они появятся снова только при blur
  vuelidate.value.$reset();

  emit('focus', event);
};

/**
 * Обработка потери фокуса
 */
const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;

  // Форматируем номер при потере фокуса
  displayValue.value = formatForDisplay(internalValue.value);

  // Валидация при blur
  vuelidate.value.$touch();

  emit('blur', event);
};

/**
 * Фильтрация клавиш - разрешены только цифры и + в начале
 */
const handleKeydown = (event: KeyboardEvent) => {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ];

  // Разрешаем служебные клавиши
  if (allowedKeys.includes(event.key)) {
    return;
  }

  // Разрешаем Ctrl/Cmd комбинации
  if (event.ctrlKey || event.metaKey) {
    return;
  }

  const target = event.target as HTMLInputElement;
  const cursorPosition = target.selectionStart || 0;
  const selectionEnd = target.selectionEnd || 0;
  const currentValue = target.value;
  const hasSelection = cursorPosition !== selectionEnd;

  // Разрешаем цифры
  if (/^\d$/.test(event.key)) {
    // Если номер уже валидный
    const phoneNumber = parsePhoneNumber(internalValue.value, props.defaultCountry);
    if (phoneNumber && phoneNumber.isValid()) {
      event.preventDefault();

      // Вычисляем новое значение
      let newValue: string;
      let newCursorPosition: number;

      if (hasSelection) {
        // Есть выделение - заменяем выделение на одну цифру
        newValue = currentValue.substring(0, cursorPosition) + event.key + currentValue.substring(selectionEnd);
        // Курсор сразу после вставленной цифры
        newCursorPosition = cursorPosition + 1;
      } else if (cursorPosition < currentValue.length) {
        // Курсор в середине - заменяем символ под курсором
        newValue = currentValue.substring(0, cursorPosition) + event.key + currentValue.substring(cursorPosition + 1);
        // Курсор сразу после замененной цифры
        newCursorPosition = cursorPosition + 1;
      } else {
        // Курсор в конце - блокируем
        return;
      }

      // Сбрасываем ошибки валидации при начале редактирования
      vuelidate.value.$reset();

      // Обновляем значение
      const normalized = normalizeInput(newValue);
      internalValue.value = normalized;
      displayValue.value = formatForDisplay(normalized);

      // Проверяем валидность и снимаем фокус или устанавливаем курсор
      void nextTick(() => {
        if (inputRef.value) {
          const newPhoneNumber = parsePhoneNumber(normalized, props.defaultCountry);
          if (newPhoneNumber && newPhoneNumber.isValid()) {
            // Номер валидный - снимаем фокус
            inputRef.value.blur();
          } else {
            // Номер невалидный - устанавливаем курсор в нужную позицию
            inputRef.value.setSelectionRange(newCursorPosition, newCursorPosition);
          }
        }
      });

      return;
    }
    return;
  }

  // Разрешаем + только в начале и если его еще нет
  if (event.key === '+' && cursorPosition === 0 && !currentValue.startsWith('+')) {
    return;
  }

  // Блокируем все остальное
  event.preventDefault();
};

/**
 * Watch для эмита validity-change
 */
watch(
  () => vuelidate.value.$error,
  (hasError) => {
    emit('validity-change', !hasError);
  },
);

/**
 * Автофокус
 */
onMounted(() => {
  if (props.autofocus) {
    void nextTick(() => {
      inputRef.value?.focus();
    });
  }
});
</script>

<template>
  <div class="relative">
    <label
      v-if="label"
      :for="inputId"
      class="mb-2 block text-sm"
      >{{ label }}</label
    >
    <input
      :id="inputId"
      ref="inputRef"
      v-model="displayValue"
      type="tel"
      class="input-class"
      :class="{
        'border-(--danger)': showErrorBorder,
      }"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown="handleKeydown"
    />

    <!-- Текст ошибки -->
    <div
      v-if="vuelidate.$error && vuelidate.$errors[0]"
      class="mt-1 text-sm text-(--danger)"
    >
      {{ vuelidate.$errors[0].$message }}
    </div>
  </div>
</template>

<style scoped>
@reference "../../../tailwind.css";

@layer components {
  .input-class {
    @apply h-[42px] w-full rounded-lg border border-(--field-border) bg-(--field-bg) px-3 text-(--text-primary) placeholder-(--text-secondary) transition-[border-color,opacity] duration-200 ease-in-out outline-none focus:border-(--accent) focus:ring-0 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50;
  }
}
</style>
